import { CommonModule } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, ClipboardList, Filter, LucideAngularModule, RefreshCw, Search } from 'lucide-angular';
import { DEFAULT_PAGE_SIZE_OPTIONS } from '../../../core/constants/pagination.constants';
import { MenuAuditLogEntry, MenuAuditLogOptions } from '../../../core/models/domain.models';
import { ToastService } from '../../../shared/ui/toast.service';
import { MenuAuditLogService } from '../services/menu-audit-log.service';

type AuditFilterState = {
  moduleName: string;
  userId: string;
  entityName: string;
  action: string;
  dateFrom: string;
  dateTo: string;
  search: string;
};

@Component({
  standalone: true,
  imports: [CommonModule, LucideAngularModule],
  templateUrl: './menu-audit-log.page.html',
  styleUrl: './menu-audit-log.page.css',
})
export class MenuAuditLogPage {
  private readonly service = inject(MenuAuditLogService);
  private readonly toast = inject(ToastService);

  protected readonly ClipboardListIcon = ClipboardList;
  protected readonly FilterIcon = Filter;
  protected readonly SearchIcon = Search;
  protected readonly RefreshCwIcon = RefreshCw;
  protected readonly ChevronsLeftIcon = ChevronsLeft;
  protected readonly ChevronLeftIcon = ChevronLeft;
  protected readonly ChevronRightIcon = ChevronRight;
  protected readonly ChevronsRightIcon = ChevronsRight;
  protected readonly pageSizes = DEFAULT_PAGE_SIZE_OPTIONS;

  protected readonly loading = signal(false);
  protected readonly rows = signal<MenuAuditLogEntry[]>([]);
  protected readonly options = signal<MenuAuditLogOptions>({ modules: [], users: [], actions: [], entities: [] });
  protected readonly expandedId = signal<string | null>(null);
  protected readonly filtersOpen = signal(false);
  protected readonly currentPage = signal(1);
  protected readonly pageSize = signal<number>(DEFAULT_PAGE_SIZE_OPTIONS[0] || 10);
  protected readonly filters = signal<AuditFilterState>({
    moduleName: '',
    userId: '',
    entityName: '',
    action: '',
    dateFrom: '',
    dateTo: '',
    search: '',
  });

  protected readonly moduleOptions = computed(() => this.options().modules);
  protected readonly userOptions = computed(() => this.options().users);
  protected readonly entityOptions = computed(() => this.options().entities);
  protected readonly actionOptions = computed(() => this.options().actions);
  protected readonly filteredRows = computed(() => {
    const term = this.filters().search.trim().toLowerCase();
    if (!term) return this.rows();
    return this.rows().filter((row) =>
      [
        row.module_name,
        row.entity_name || '',
        row.entity_id,
        row.action,
        row.changed_by_name || '',
        row.changed_by_role || '',
        JSON.stringify(row.old_data || {}),
        JSON.stringify(row.new_data || {}),
      ]
        .join(' ')
        .toLowerCase()
        .includes(term),
    );
  });
  protected readonly totalPages = computed(() => Math.max(1, Math.ceil(this.filteredRows().length / this.pageSize())));
  protected readonly pagedRows = computed(() => {
    const page = Math.min(this.currentPage(), this.totalPages());
    const start = (page - 1) * this.pageSize();
    return this.filteredRows().slice(start, start + this.pageSize());
  });
  protected readonly startRow = computed(() => (this.filteredRows().length === 0 ? 0 : (this.currentPage() - 1) * this.pageSize() + 1));
  protected readonly endRow = computed(() => Math.min(this.currentPage() * this.pageSize(), this.filteredRows().length));

  constructor() {
    this.loadOptions();
    this.load();
  }

  protected setFilter<K extends keyof AuditFilterState>(key: K, value: AuditFilterState[K]): void {
    this.filters.update((current) => ({ ...current, [key]: value }));
  }

  protected applyFilters(): void {
    this.currentPage.set(1);
    this.load();
  }

  protected resetFilters(): void {
    this.filters.set({
      moduleName: '',
      userId: '',
      entityName: '',
      action: '',
      dateFrom: '',
      dateTo: '',
      search: '',
    });
    this.currentPage.set(1);
    this.load();
  }

  protected refresh(): void {
    this.currentPage.set(1);
    this.load();
  }

  protected toggleFilters(): void {
    this.filtersOpen.update((open) => !open);
  }

  protected nextPage(): void {
    this.currentPage.update((page) => Math.min(page + 1, this.totalPages()));
  }

  protected previousPage(): void {
    this.currentPage.update((page) => Math.max(page - 1, 1));
  }

  protected setPageSize(value: string): void {
    const next = Number(value || 10);
    this.pageSize.set(Number.isFinite(next) && next > 0 ? next : 10);
    this.currentPage.set(1);
  }

  protected setPage(page: number): void {
    this.currentPage.set(Math.max(1, Math.min(page, this.totalPages())));
  }

  protected toggleExpand(rowId: string): void {
    this.expandedId.update((current) => (current === rowId ? null : rowId));
  }

  protected isExpanded(rowId: string): boolean {
    return this.expandedId() === rowId;
  }

  protected oldKeys(row: MenuAuditLogEntry): string[] {
    return this.orderedSnapshotKeys(row.old_data);
  }

  protected newKeys(row: MenuAuditLogEntry): string[] {
    return this.orderedSnapshotKeys(row.new_data);
  }

  protected isChangedKey(row: MenuAuditLogEntry, key: string): boolean {
    return this.snapshotComparableValue(row.old_data?.[key]) !== this.snapshotComparableValue(row.new_data?.[key]);
  }

  protected rowMenuName(row: MenuAuditLogEntry): string {
    return this.snapshotString(row, 'menu_name') || row.entity_name || '-';
  }

  protected rowUserType(row: MenuAuditLogEntry): string {
    return this.snapshotString(row, 'user_type') || '-';
  }

  protected rowMenuUrl(row: MenuAuditLogEntry): string {
    return this.snapshotString(row, 'menu_url') || '-';
  }

  protected rowMenuPriority(row: MenuAuditLogEntry): string {
    const value = this.snapshotValue(row, 'menu_priority');
    return value === null || value === undefined || value === '' ? '-' : String(value);
  }

  protected rowMenuList(row: MenuAuditLogEntry): string {
    return this.snapshotString(row, 'menu_list') || '-';
  }

  protected rowParentLabel(row: MenuAuditLogEntry): string {
    return this.snapshotString(row, 'parent_name') || '-';
  }

  protected rowMenuAlias(row: MenuAuditLogEntry): string {
    return this.snapshotString(row, 'menu_alias') || '-';
  }

  protected rowMenuIcon(row: MenuAuditLogEntry): string {
    return this.snapshotString(row, 'menu_icon') || '-';
  }

  protected rowSendImage(row: MenuAuditLogEntry): string {
    return this.snapshotString(row, 'send_image') || '-';
  }

  protected rowIsDefault(row: MenuAuditLogEntry): string {
    return this.snapshotBooleanLabel(row, 'is_default');
  }

  protected rowIsShow(row: MenuAuditLogEntry): string {
    return this.snapshotBooleanLabel(row, 'is_show');
  }

  protected rowIsActive(row: MenuAuditLogEntry): string {
    return this.snapshotBooleanLabel(row, 'active');
  }

  private snapshotValue(row: MenuAuditLogEntry, key: string): unknown {
    const primary = row.action === 'DELETE' ? row.old_data : row.new_data;
    const secondary = row.action === 'DELETE' ? row.new_data : row.old_data;
    return primary?.[key] ?? secondary?.[key] ?? null;
  }

  private snapshotString(row: MenuAuditLogEntry, key: string): string {
    const value = this.snapshotValue(row, key);
    return value === null || value === undefined || value === '' ? '' : String(value);
  }

  private snapshotBooleanLabel(row: MenuAuditLogEntry, key: string): string {
    const value = this.snapshotValue(row, key);
    if (value === null || value === undefined || value === '') return '-';
    return value ? 'Yes' : 'No';
  }

  private snapshotComparableValue(value: unknown): string {
    if (value === null || value === undefined) return '';
    return JSON.stringify(value);
  }

  private orderedSnapshotKeys(snapshot?: Record<string, unknown> | null): string[] {
    const keys = Object.keys(snapshot || {});
    const preferredOrder = [
      'menu_name',
      'user_type',
      'menu_url',
      'menu_priority',
      'menu_list',
      'parent_name',
      'parent_id',
      'level',
      'menu_alias',
      'menu_icon',
      'send_image',
      'is_default',
      'is_show',
      'active',
    ];
    return keys.sort((a, b) => {
      const left = preferredOrder.indexOf(a);
      const right = preferredOrder.indexOf(b);
      if (left === -1 && right === -1) return a.localeCompare(b);
      if (left === -1) return 1;
      if (right === -1) return -1;
      return left - right;
    });
  }

  private load(): void {
    this.loading.set(true);
    const current = this.filters();
    this.service
      .list({
        module_name: current.moduleName || undefined,
        user_id: current.userId || undefined,
        entity_name: current.entityName || undefined,
        action: current.action || undefined,
        date_from: current.dateFrom || undefined,
        date_to: current.dateTo || undefined,
        limit: '1000',
      })
      .subscribe({
        next: (rows) => {
          this.loading.set(false);
          this.rows.set(rows || []);
          this.currentPage.set(1);
        },
        error: () => {
          this.loading.set(false);
          this.rows.set([]);
          this.toast.show('Failed to load menu audit logs', '', 'danger');
        },
      });
  }

  private loadOptions(): void {
    this.service.options().subscribe({
      next: (options) => this.options.set(options || { modules: [], users: [], actions: [], entities: [] }),
      error: () => this.options.set({ modules: [], users: [], actions: [], entities: [] }),
    });
  }
}
